import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Send, Trash2, MessageSquare, Pencil, Check, X } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { CategoryPill } from "@/components/CategoryPill";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { fullName, timeAgo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export function PostCard({ post, onChange, onDelete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [liking, setLiking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.content);
  const [savingEdit, setSavingEdit] = useState(false);

  const isMine = user && post.author?.id === user.id;
  const isAdmin = user?.role === "admin";
  const canModify = isMine || isAdmin;

  const like = async () => {
    if (liking) return;
    setLiking(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      onChange && onChange(data);
    } catch (e) {
      toast.error("Could not update like");
    } finally {
      setLiking(false);
    }
  };

  const addComment = async () => {
    if (!commentText.trim() || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { content: commentText.trim() });
      onChange && onChange(data);
      setCommentText("");
    } catch {
      toast.error("Could not post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const removePost = async () => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      onDelete && onDelete(post.id);
      toast.success("Post deleted");
    } catch {
      toast.error("Could not delete post");
    }
  };

  const startEdit = () => {
    setEditText(post.content);
    setEditing(true);
  };

  const saveEdit = async () => {
    const trimmed = editText.trim();
    if (!trimmed || savingEdit) return;
    if (trimmed === post.content) {
      setEditing(false);
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/posts/${post.id}`, { content: trimmed, category: post.category });
      onChange && onChange(data);
      setEditing(false);
      toast.success("Post updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save changes");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <article
      className="bg-white rounded-2xl border border-border cornell-shadow p-5 fade-in"
      data-testid={`post-card-${post.id}`}
    >
      <header className="flex items-start gap-3">
        <Link to={`/profile/${post.author?.id}`} data-testid={`post-author-link-${post.id}`}>
          <UserAvatar user={post.author} size={44} />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/profile/${post.author?.id}`}
              className="font-semibold text-[15px] text-neutral-900 hover:underline"
              data-testid={`post-author-name-${post.id}`}
            >
              {fullName(post.author)}
            </Link>
            {post.author?.role === "admin" && (
              <span className="text-[10px] uppercase tracking-wider bg-[#B31B1B] text-white px-1.5 py-0.5 rounded" data-testid={`post-admin-badge-${post.id}`}>Admin</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {post.author?.college} · Class of {post.author?.grad_year} · {timeAgo(post.created_at)}
            {post.edited && (
              <span className="ml-1 italic text-neutral-500" data-testid={`post-edited-label-${post.id}`} title={post.edited_at ? `Edited ${timeAgo(post.edited_at)}` : "Edited"}>
                (edited)
              </span>
            )}
          </div>
        </div>
        <CategoryPill category={post.category} />
      </header>

      {editing ? (
        <div className="mt-4" data-testid={`post-edit-form-${post.id}`}>
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={3}
            maxLength={2000}
            className="resize-none"
            data-testid={`post-edit-textarea-${post.id}`}
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditing(false)}
              className="gap-1.5"
              data-testid={`post-edit-cancel-${post.id}`}
            >
              <X className="w-4 h-4" /> Cancel
            </Button>
            <Button
              size="sm"
              onClick={saveEdit}
              disabled={savingEdit || !editText.trim()}
              className="bg-[#B31B1B] hover:bg-[#8f1616] gap-1.5"
              data-testid={`post-edit-save-${post.id}`}
            >
              <Check className="w-4 h-4" /> {savingEdit ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-4 text-[15px] leading-relaxed text-neutral-800 whitespace-pre-wrap" data-testid={`post-content-${post.id}`}>
          {post.content}
        </p>
      )}

      <div className="mt-4 flex items-center gap-1 text-neutral-600">
        <Button
          variant="ghost"
          size="sm"
          onClick={like}
          className={`gap-1.5 rounded-full ${post.liked_by_me ? "text-[#B31B1B]" : ""}`}
          data-testid={`post-like-btn-${post.id}`}
        >
          <Heart className={`w-4 h-4 ${post.liked_by_me ? "fill-[#B31B1B]" : ""}`} />
          <span className="text-sm font-medium">{post.like_count || 0}</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowComments((v) => !v)}
          className="gap-1.5 rounded-full"
          data-testid={`post-comment-toggle-${post.id}`}
        >
          <MessageCircle className="w-4 h-4" />
          <span className="text-sm font-medium">{post.comment_count || 0}</span>
        </Button>
        <div className="flex-1" />
        {user && post.author && post.author.id !== user.id && post.author.dm_preference !== "none" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/messages/${post.author.id}`)}
            className="text-neutral-500 hover:text-[#B31B1B]"
            title={`Message ${post.author.first_name}`}
            data-testid={`post-message-author-btn-${post.id}`}
          >
            <MessageSquare className="w-4 h-4" />
          </Button>
        )}
        {canModify && !editing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={startEdit}
            className="text-neutral-400 hover:text-[#B31B1B]"
            title="Edit post"
            data-testid={`post-edit-btn-${post.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
        )}
        {canModify && (
          <Button
            variant="ghost"
            size="sm"
            onClick={removePost}
            className="text-neutral-400 hover:text-[#B31B1B]"
            title="Delete post"
            data-testid={`post-delete-btn-${post.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {showComments && (
        <div className="mt-3 border-t border-border pt-3 space-y-3" data-testid={`post-comments-${post.id}`}>
          {(post.comments || []).map((c) => (
            <CommentItem key={c.id} comment={c} postId={post.id} onChange={onChange} />
          ))}
          <div className="flex gap-2 items-end">
            <Textarea
              rows={1}
              placeholder="Write a comment…"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              className="min-h-[40px] resize-none"
              data-testid={`comment-input-${post.id}`}
            />
            <Button
              size="sm"
              onClick={addComment}
              disabled={submitting || !commentText.trim()}
              className="bg-[#B31B1B] hover:bg-[#8f1616]"
              data-testid={`comment-submit-${post.id}`}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </article>
  );
}

function CommentItem({ comment, postId, onChange }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(comment.content);
  const [saving, setSaving] = useState(false);

  const canModify = user && (comment.author?.id === user.id || user.role === "admin");

  const save = async () => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;
    if (trimmed === comment.content) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/posts/${postId}/comments/${comment.id}`, { content: trimmed });
      onChange && onChange(data);
      setEditing(false);
      toast.success("Comment updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save comment");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      const { data } = await api.delete(`/posts/${postId}/comments/${comment.id}`);
      onChange && onChange(data);
      toast.success("Comment deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete comment");
    }
  };

  return (
    <div className="flex gap-2.5" data-testid={`comment-${comment.id}`}>
      <UserAvatar user={comment.author} size={30} />
      <div className="flex-1 bg-neutral-50 rounded-xl px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div className="text-xs font-semibold text-neutral-800">{fullName(comment.author)}</div>
          {canModify && !editing && (
            <div className="flex items-center gap-0.5 -mr-1">
              <button
                onClick={() => { setText(comment.content); setEditing(true); }}
                className="p-1 rounded hover:bg-neutral-200 text-neutral-400 hover:text-[#B31B1B]"
                title="Edit comment"
                data-testid={`comment-edit-btn-${comment.id}`}
              >
                <Pencil className="w-3 h-3" />
              </button>
              <button
                onClick={remove}
                className="p-1 rounded hover:bg-neutral-200 text-neutral-400 hover:text-[#B31B1B]"
                title="Delete comment"
                data-testid={`comment-delete-btn-${comment.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
        {editing ? (
          <div className="mt-1" data-testid={`comment-edit-form-${comment.id}`}>
            <Textarea
              rows={2}
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={500}
              className="resize-none text-sm min-h-[40px]"
              data-testid={`comment-edit-textarea-${comment.id}`}
            />
            <div className="mt-1.5 flex justify-end gap-1">
              <button
                onClick={() => setEditing(false)}
                className="text-xs px-2 py-1 rounded hover:bg-neutral-200 text-neutral-600"
                data-testid={`comment-edit-cancel-${comment.id}`}
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || !text.trim()}
                className="text-xs px-2 py-1 rounded bg-[#B31B1B] text-white hover:bg-[#8f1616] disabled:opacity-50"
                data-testid={`comment-edit-save-${comment.id}`}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-neutral-700 whitespace-pre-wrap">{comment.content}</div>
        )}
        <div className="text-[10px] text-muted-foreground mt-0.5">
          {timeAgo(comment.created_at)}
          {comment.edited && (
            <span className="ml-1 italic" data-testid={`comment-edited-label-${comment.id}`}>(edited)</span>
          )}
        </div>
      </div>
    </div>
  );
}
